import { AiUsageRecordRow, AiUsageRecordsResponse, FfmpegUsageResponse, FfmpegUsageTask } from "../types/MeGenTypes";
import { AI_USAGE_PAGE_SIZE, AiUsageLoadError, AiUsageProgress, loadAllAiUsageRecords } from "./aiUsageLoading";
import { formatMetric, formatMoney } from "./aiUsagePivot";

export const knownTaskValue = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

export const isUsageError = (row: AiUsageRecordRow): boolean =>
  row.ffmpeg_task ? row.ffmpeg_task.status === "error" : !!row.status && row.status !== "success";

export const taskCurrencyOf = (task: FfmpegUsageTask): string => task.worker_run?.rate_currency?.trim().toUpperCase() || "";

export const knownTaskCost = (task: FfmpegUsageTask): boolean => knownTaskValue(task.allocated_cost) && !!taskCurrencyOf(task);

export const formatTaskCost = (task: FfmpegUsageTask, unknown: string): string => {
  if (!knownTaskCost(task)) return unknown;
  const currency = taskCurrencyOf(task);
  return currency === "USD" ? formatMoney(task.allocated_cost!) : `${formatMetric(task.allocated_cost!)} ${currency}`;
};

export const compareUsageRecordsNewestFirst = (a: AiUsageRecordRow, b: AiUsageRecordRow): number => {
  const timestamp = (row: AiUsageRecordRow) => {
    const value = row.created_at ? new Date(row.created_at).getTime() : 0;
    return Number.isFinite(value) ? value : 0;
  };
  return timestamp(b) - timestamp(a) || String(b.id).localeCompare(String(a.id), undefined, { numeric: true });
};

export const recordOfFfmpegTask = (task: FfmpegUsageTask): AiUsageRecordRow => ({
  id: `ffmpeg:${task.id}`,
  ffmpeg_task: task,
  thread_id: task.thread_id,
  user_id: task.user_id,
  user_name: task.user_name,
  service: "ffmpeg",
  provider: "gpuhub",
  model: "ffmpeg",
  caller: task.output_key.split("/").pop() || "ffmpeg",
  status: task.status === "done" ? "success" : task.status || "unknown",
  duration_ms: knownTaskValue(task.allocated_instance_seconds) ? task.allocated_instance_seconds * 1000 : null,
  input_tokens: 0,
  output_tokens: 0,
  input_characters: 0,
  audio_duration: 0,
  video_duration: 0,
  requested_duration: null,
  resolution: null,
  image_count: 0,
  // Backend task timestamps use UTC, including legacy naive database values.
  created_at: task.created_at && !/(Z|[+-]\d\d:\d\d)$/i.test(task.created_at) ? `${task.created_at}Z` : task.created_at,
  error_message: task.error_message,
});

export const loadAllFfmpegUsageTasks = async (
  fetchPage: (limit: number, offset: number) => Promise<FfmpegUsageResponse>,
  isCurrent: () => boolean,
  onProgress: (progress: AiUsageProgress) => void
): Promise<FfmpegUsageTask[]> => {
  const tasks = new Map<string, FfmpegUsageTask>();
  let total: number | null = null;
  let offset = 0;
  let lastTimestamp = Infinity;
  while (true) {
    if (!isCurrent()) throw new AiUsageLoadError("cancelled", "FFmpeg loading cancelled");
    const page = await fetchPage(AI_USAGE_PAGE_SIZE, offset);
    if (!isCurrent()) throw new AiUsageLoadError("cancelled", "FFmpeg loading cancelled");
    if (!Number.isSafeInteger(page.total) || page.total < 0 || (total !== null && total !== page.total) ||
      page.offset !== offset || !Array.isArray(page.items) || page.items.length > AI_USAGE_PAGE_SIZE) {
      throw new AiUsageLoadError("inconsistent", "Unexpected FFmpeg page or changed total");
    }
    total = page.total;
    for (const task of page.items) {
      if (!task || typeof task.id !== "string" || !task.id || tasks.has(task.id) || task.task_type !== "ffmpeg") {
        throw new AiUsageLoadError("inconsistent", "Duplicate/invalid FFmpeg task or unexpected ComfyUI generation");
      }
      if (task.created_at) {
        const timestamp = new Date(recordOfFfmpegTask(task).created_at!).getTime();
        if (!Number.isFinite(timestamp) || timestamp > lastTimestamp) throw new AiUsageLoadError("inconsistent", "FFmpeg tasks are not ordered newest first");
        lastTimestamp = timestamp;
      }
      tasks.set(task.id, task);
    }
    onProgress({ loaded: tasks.size, total });
    if (tasks.size > total) throw new AiUsageLoadError("inconsistent", "FFmpeg count exceeded total");
    if (tasks.size === total) return Array.from(tasks.values());
    if (!page.items.length) throw new AiUsageLoadError("inconsistent", "FFmpeg page ended before total");
    offset += page.items.length;
  }
};

export const loadAllUsageSources = async (
  fetchAiPage: (limit: number, offset: number) => Promise<AiUsageRecordsResponse>,
  fetchTaskPage: (limit: number, offset: number) => Promise<FfmpegUsageResponse>,
  isCurrent: () => boolean,
  onProgress: (progress: AiUsageProgress) => void
): Promise<AiUsageRecordRow[]> => {
  let active = true;
  const sourceIsCurrent = () => active && isCurrent();
  const progress: AiUsageProgress[] = [{ loaded: 0, total: null }, { loaded: 0, total: null }];
  const update = (source: number) => (next: AiUsageProgress) => {
    if (!sourceIsCurrent()) return;
    progress[source] = next;
    onProgress({ loaded: progress[0].loaded + progress[1].loaded,
      total: progress.every((value) => value.total !== null) ? progress[0].total! + progress[1].total! : null });
  };
  try {
    const [ai, tasks] = await Promise.all([
      loadAllAiUsageRecords(fetchAiPage, sourceIsCurrent, update(0)),
      loadAllFfmpegUsageTasks(fetchTaskPage, sourceIsCurrent, update(1)),
    ]);
    if (!isCurrent()) throw new AiUsageLoadError("cancelled", "Usage loading cancelled");
    return [...ai, ...tasks.map(recordOfFfmpegTask)].sort(compareUsageRecordsNewestFirst);
  } finally {
    // A rejected source stops its sibling before publishing progress or fetching another page.
    active = false;
  }
};
